import { fetchCompetitionTournaments, fetchMatches, optaMatchStatus, optaPageSize } from "@/helpers/Opta";
import { argentinaLigaCopa, optaStage } from "../constants";
import {
    findCalendarIdByName,
    findMenuCompetition,
    hasRegularSeason,
    isCupCompetition,
    isLeagueStageMatch,
    isSecondPhaseMatch,
    pickCalendarId,
    toCompetitionHeader,
} from "../competitionRules";

/**
 * Datos de la página de una competición.
 *
 * El formato del torneo decide qué se resuelve en el servidor:
 *  - copa / fase regular -> todos los partidos de la temporada
 *  - liga                -> sólo la jornada actual y la última (el fixture lo pide la vista)
 *
 * Devuelve `{}` cuando la competición no está en el menú o el feed no la trae:
 * la vista lo interpreta como "sin información".
 */
export const loadCompetitionPage = async ({ competitionSlug, competitionsToMap }) => {
    const menuCompetition = findMenuCompetition(competitionSlug, competitionsToMap); // datos desde el CMS

    if (!menuCompetition?.id) return {};

    const calendars = await fetchCompetitionTournaments({ competitionId: menuCompetition.id, withStages: true });
    const competition = calendars?.competition?.[0];
    if (!competition) return {};

    const isCup = isCupCompetition(competition, menuCompetition);
    const isRegularSeason = hasRegularSeason(competition);

    const fixture =
        isCup || isRegularSeason
            ? await loadSeasonFixture({ competition, menuCompetition, isRegularSeason })
            : await loadLeagueWeeks({ competition, menuCompetition });

    const competitionData = toCompetitionHeader(competition);

    // La Copa de la Liga es una fase del torneo argentino, así que Opta la devuelve con el
    // nombre del torneo. El nombre editorial se aplica acá, sobre el objeto que arma este
    // loader, y no en los componentes
    if (fixture.isArgentinaLigaCopa) {
        competitionData.name = argentinaLigaCopa.displayName;
    }

    return { ...fixture, isCup, competitionData };
};

/** Copas y fases regulares se pintan a partir del fixture completo de la temporada. */
const loadSeasonFixture = async ({ competition, menuCompetition, isRegularSeason }) => {
    const calendarId = pickCalendarId(competition, menuCompetition.calendar ?? new Date().getFullYear());
    const fixture = await fetchMatches({
        calendarId,
        status: optaMatchStatus.all,
        pageSize: optaPageSize.cupSeason,
    });

    if (!isRegularSeason) {
        return { fixtureCupCompetition: fixture ?? [] };
    }

    // La fase regular sólo necesita el resultado de cada partido, no el detalle completo.
    return {
        isRegularSeason,
        fixtureCupCompetition: (fixture?.match ?? []).map(({ matchInfo, liveData }) => ({
            id: matchInfo?.id,
            week: matchInfo?.week,
            contestant: matchInfo?.contestant,
            status: liveData?.matchDetails?.matchStatus,
        })),
    };
};

const loadLeagueWeeks = async ({ competition, menuCompetition }) => {
    const isArgentinaLigaCopa = Boolean(menuCompetition.isArgentinaLigaCopa);

    const weeks = isArgentinaLigaCopa
        ? await resolveWeeks({
            calendarId: findCalendarIdByName(competition, argentinaLigaCopa.calendarName),
            pageSize: optaPageSize.ligaCopaSeason,
            nextMatchStatus: optaMatchStatus.fixtureOrPlayed,
            lastWeekStage: optaStage.secondPhase,
            countsAsCurrentWeek: isSecondPhaseMatch,
        })
        : await resolveWeeks({
            calendarId: competition.tournamentCalendar?.[0]?.id,
            pageSize: optaPageSize.leagueSeason,
            nextMatchStatus: optaMatchStatus.fixtureOrPlaying,
            countsAsCurrentWeek: isLeagueStageMatch,
        });

    return { ...weeks, isArgentinaLigaCopa };
};

/**
 * Jornada (fecha) en curso y última jornada de una liga.
 * Se piden en paralelo la temporada completa (para la última jornada) y el próximo partido
 * (para la jornada en curso); si la etapa de ese partido no cuenta, se usa la última.
 * Se necesita para pintar el selector de fechas (weeks) y para pedir el partido de la jornada en curso.
 */
const resolveWeeks = async ({ calendarId, pageSize, nextMatchStatus, lastWeekStage, countsAsCurrentWeek }) => {
    const [season, nextMatches] = await Promise.all([
        fetchMatches({ calendarId, status: optaMatchStatus.all, pageSize }),
        fetchMatches({ calendarId, status: nextMatchStatus, pageSize: optaPageSize.firstMatch }),
    ]);

    const lastWeek = getLastWeek(season?.match, lastWeekStage);
    const nextMatch = nextMatches?.match?.[0];

    if (!nextMatch) return { actualWeek: 1, lastWeek };

    return {
        actualWeek: countsAsCurrentWeek(nextMatch) ? nextMatch.matchInfo?.week : lastWeek,
        lastWeek,
    };
};

/** Última jornada publicada. Las jornadas llegan como string; el resto se descarta. */
const getLastWeek = (matches = [], stageName) => {
    const weeks = (matches ?? [])
        .filter((match) => !stageName || match?.matchInfo?.stage?.name === stageName)
        .map((match) => match?.matchInfo?.week)
        .filter((week) => typeof week === "string");

    return weeks.length > 0 ? Math.max(...weeks) : null;
};
